/**
 * Where a preview image actually sits on the ground.
 *
 * Previews are normally pinned to the bounding box of the footprint, which is
 * right only while the product is north-up. Ground-range SAR is not: an ICEYE
 * GRD raster is a rectangle tilted to the flight path, so its bounding box is a
 * different, larger shape — pinning the preview there both straightens the image
 * out and stretches it past where the image really is. On a typical SCAN frame
 * that is a 12° rotation and 41% more ground covered than the image holds.
 *
 * The raster carries its own georeferencing, so rather than guessing which
 * footprint vertex is which corner, this reads it.
 */

/**
 * Which provider's placement rules apply to this image.
 *
 * Taken from the image itself first, and only then from the argument the caller
 * passed: that argument is the search context, and call sites forget it. The
 * tasking order page never passed one, so its images silently fell back to the
 * default placement no matter who took them.
 */
export const resolveProviderCode = (item: any, providerCode?: string): string =>
   String(item?.provider_code ?? item?.properties?.provider_code ?? providerCode ?? "")
      .trim()
      .toLowerCase();

/** A `[lon, lat]` pair, degrees. */
export type Corner = [number, number];

/**
 * How far the corners derived from the georeferencing may sit from the declared
 * footprint before they are treated as nonsense, as a fraction of the
 * footprint's longer side.
 *
 * The two are computed differently — the footprint usually comes from the RPC
 * model over terrain, the transform is a plain affine — so they disagree by a
 * couple of hundred metres at the corners, well inside this. What it is there to
 * catch is a transform written in some other convention, which lands the corners
 * kilometres away or off the planet entirely.
 */
const AGREEMENT_TOLERANCE = 0.02;

/**
 * The image's four corners in the order a map image source wants them:
 * **top-left, top-right, bottom-right, bottom-left of the raster**.
 *
 * For a tilted product those are not the north-west, north-east, south-east and
 * south-west corners, and the difference is a 90° rotation — which is why this
 * returns them already ordered rather than leaving a caller to sort geographic
 * corners into image corners.
 *
 * Returns null when the item does not carry usable georeferencing, leaving the
 * caller on the bounding box it would have used anyway.
 */
export const getImageCorners = (item: any): Corner[] | null =>
{
   const transform = item?.properties?.["proj:transform"];
   const shape = item?.properties?.["proj:shape"];

   if (!Array.isArray(transform) || transform.length < 6) return null;
   if (!Array.isArray(shape) || shape.length < 2) return null;

   // GDAL's GeoTransform layout: origin first, then the two axis vectors. The
   // off-diagonal terms are the rotation, and they are zero for a north-up
   // product — which is why the bounding box has worked for everything else.
   const [originLon, lonPerCol, lonPerRow, originLat, latPerCol, latPerRow] =
      transform.map(Number);

   // `proj:shape` is [height, width], i.e. rows then columns.
   const [rows, cols] = shape.map(Number);

   const numbers = [
      originLon, lonPerCol, lonPerRow, originLat, latPerCol, latPerRow, rows, cols,
   ];

   if (!numbers.every((value) => Number.isFinite(value))) return null;
   if (rows <= 0 || cols <= 0) return null;

   const at = (col: number, row: number): Corner => [
      originLon + lonPerCol * col + lonPerRow * row,
      originLat + latPerCol * col + latPerRow * row,
   ];

   const corners: Corner[] = [
      at(0, 0),        // raster top-left
      at(cols, 0),     // top-right
      at(cols, rows),  // bottom-right
      at(0, rows),     // bottom-left
   ];

   return agreesWithFootprint(corners, item?.geometry) ? corners : null;
};

/**
 * Do these corners describe the same patch of ground as the declared footprint?
 *
 * Compared as bounding boxes rather than vertex by vertex: the two are allowed
 * to name their corners in different orders, and that is precisely the thing
 * this module exists to sort out.
 */
const agreesWithFootprint = (corners: Corner[], geometry: any): boolean =>
{
   const declared = boundsOf(flattenPositions(geometry?.coordinates));
   const derived = boundsOf(corners);

   if (!declared || !derived) return false;

   const span = Math.max(
      declared.maxX - declared.minX,
      declared.maxY - declared.minY,
   );

   if (!(span > 0)) return false;

   const limit = span * AGREEMENT_TOLERANCE;

   return (
      Math.abs(declared.minX - derived.minX) <= limit &&
      Math.abs(declared.maxX - derived.maxX) <= limit &&
      Math.abs(declared.minY - derived.minY) <= limit &&
      Math.abs(declared.maxY - derived.maxY) <= limit
   );
};

/** Every `[x, y]` inside an arbitrarily nested GeoJSON coordinate array. */
const flattenPositions = (coordinates: any): number[][] =>
{
   const positions: number[][] = [];

   const walk = (node: any) =>
   {
      if (!Array.isArray(node)) return;

      if (typeof node[0] === "number") positions.push(node);
      else node.forEach(walk);
   };

   walk(coordinates);

   return positions;
};

const boundsOf = (positions: number[][]) =>
{
   if (!positions.length) return null;

   let minX = Infinity;
   let minY = Infinity;
   let maxX = -Infinity;
   let maxY = -Infinity;

   for (const [x, y] of positions)
   {
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
   }

   return { minX, minY, maxX, maxY };
};
